<!--
This file is part of the project: "Digiziege"
An app for animal-welfare-oriented goat husbandry and contactless weight estimation.
GitLab: https://gitlab.rlp.net/proj-wise2526-digiziege/digiziege

Copyright (c) 2026 Digiziege Team TH-Bingen

Description:
Companion app for welfare-oriented goat husbandry featuring contactless weight estimation via camera,
master data management and health tracking.
Built as a PWA with a local-first approach to ensure data privacy.

LICENSE INFORMATION:
This program is free software: you can redistribute it and/or modify it under the terms of the
GNU Affero General Public License as published by the Free Software Foundation,
either version 3 of the License, or (at your option) any later version.

This program is distributed in the hope that it will be useful, but WITHOUT ANY WARRANTY;
without even the implied warranty of MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.
See the GNU Affero General Public License for more details.

You should have received a copy of the GNU Affero General Public License along with this program.
If not, see <https://www.gnu.org/licenses/>.
-->

<template>
  <ModuleShell
    :title="t('views.selectedGoat.weight.listTitle')"
    :icon="List"
    class="h-fit"
    hideAction>
    <ValueListTable
      :data="weights"
      :columns="columns"
      :loading="false"
      @refresh="refreshWeights">
      <template #toolbar-actions>
        <button
          @click="openCamera"
          class="btn-icon h-[42px] px-3 rounded-lg flex items-center gap-2 font-medium transition-colors"
          :style="{
            backgroundColor: 'var(--color-primary-200)',
            color: 'var(--color-primary-700)',
            border: '1px solid var(--color-primary-400)',
          }">
          <Camera class="w-5 h-5" />
        </button>

        <button
          @click="openAddDialog"
          class="btn h-[42px] px-4 rounded-lg flex items-center gap-2 font-medium text-white shadow-sm transition-transform active:scale-95"
          :style="{
            backgroundColor: 'var(--color-success)',
            border: '1px solid var(--color-success)',
          }">
          <Plus class="w-5 h-5" />
          <span>{{ t('common.buttons.add') }}</span>
        </button>
      </template>

      <template #cell-value="{ item }">
        <span class="font-bold text-[var(--color-primary-900)]">
          {{ (item as WeightEntry).value }}
        </span>
        <span class="text-xs text-[var(--color-primary-500)] ml-1">kg</span>
      </template>

      <template #cell-measuredAt="{ item }">
        {{
          new Date((item as WeightEntry).measuredAt).toLocaleDateString(
            'de-DE',
            {
              day: '2-digit',
              month: '2-digit',
              year: 'numeric',
            }
          )
        }}
      </template>

      <template #cell-actions="{ item }">
        <div class="flex items-center justify-end gap-2">
          <button
            @click.stop="openEditDialog(item as WeightEntry)"
            class="h-8 w-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer bg-[var(--color-primary-200)] text-[var(--color-primary-700)] hover:bg-[var(--color-primary-300)]"
            :title="t('common.buttons.edit')">
            <Pencil class="w-4 h-4" />
          </button>

          <button
            @click.stop="openDeleteDialog(item as WeightEntry)"
            class="h-8 w-8 flex items-center justify-center rounded-lg transition-colors cursor-pointer bg-[var(--color-primary-200)] text-[var(--color-error)] hover:bg-[var(--color-error)] hover:text-white"
            :title="t('common.buttons.delete')">
            <Trash2 class="w-4 h-4" />
          </button>
        </div>
      </template>
    </ValueListTable>

    <Teleport to="body">
      <ConfirmActionModule
        v-if="showConfirm && selectedWeight"
        :message="
          t('views.selectedGoat.weight.deleteAskMessage', {
            date: formatDate(selectedWeight.measuredAt),
            weight: selectedWeight.value,
          })
        "
        :confirmMessage="$t('common.buttons.delete')"
        variant="danger"
        :confirmLabel="t('common.buttons.delete')"
        @cancel="closeConfirm"
        @confirm="deleteConfirmed" />
    </Teleport>
  </ModuleShell>

  <ValueInput
    :show="showDialog"
    :title="isEditing ? t('common.buttons.edit') : goat?.eartag || ''"
    :subtitle="goat?.name || ''"
    :inputLabel="$t('views.session.weight.label')"
    :placeholder="$t('views.session.weight.placeholder')"
    :inputMode="'decimal'"
    :goatId="goatId"
    :validation="{ type: 'range', min: 1, max: 200, unit: 'kg' }"
    :showDateInput="true"
    :initialValue="isEditing ? selectedWeight?.value : null"
    @save="handleWeightSaved"
    @cancel="showDialog = false" />

  <PhotoWeightView
    ref="photoWeightRef"
    @result="handleWeightResult"
    @error="handlePhotoWeightError" />

  <Toast
    :show="showToast"
    :message="toastMessage"
    :subtitle="toastSubtitle"
    :show-subtitle="!!toastSubtitle"
    :type="toastType"
    :duration="toastDuration" />
</template>

<script setup lang="ts">
  import { ref, computed } from 'vue'
  import { type PropType } from 'vue'
  import { useI18n } from 'vue-i18n'
  import ModuleShell from '@/components/ui/layout/ModuleShell.vue'
  import ValueListTable from '@/components/ui/tables/ValueListTable.vue'
  import ValueInput from '@/components/ui/inputs/ValueInput.vue'
  import PhotoWeightView from '@/components/camera/ml/PhotoWeightView.vue'
  import Toast from '@/components/ui/feedback/Toast.vue'
  import ConfirmActionModule from '@/components/actions/ConfirmAction.vue'
  import { List, Camera, Plus, Pencil, Trash2 } from 'lucide-vue-next'
  import { formatDate } from '@/utils/format/dateformatting'
  import { useWeightStore } from '@/stores/weight.store.js'
  import { useGoatStore } from '@/stores/goat.store.js'

  interface WeightEntry {
    id: number
    value: number
    measuredAt: string
    [key: string]: any
  }

  const props = defineProps({
    weights: {
      type: Array as PropType<WeightEntry[]>,
      required: true,
    },
    goatId: { type: Number, required: true },
    refreshWeights: {
      type: Function as PropType<() => Promise<void> | void>,
      required: true,
    },
  })

  const { t } = useI18n()
  const weightStore = useWeightStore()
  const goatStore = useGoatStore()

  const goat = computed(() => {
    return goatStore.goats.find((g) => g.id === props.goatId)
  })

  // State Variables
  const showDialog = ref(false)
  const showConfirm = ref(false)
  const isEditing = ref(false)
  const selectedWeight = ref<WeightEntry | null>(null)
  const photoWeightRef = ref(null)

  // Toast notification state
  const showToast = ref(false)
  const toastMessage = ref('')
  const toastType = ref('saved')
  const toastDuration = ref(3000)
  const toastSubtitle = ref('')

  const columns = [
    {
      key: 'measuredAt',
      label: t('common.valueTableList.weightDate'),
      type: 'date',
    },
    {
      key: 'value',
      label: t('common.valueTableList.weightValue'),
      center: true,
      type: 'number',
    },
    { key: 'actions', label: '', right: true },
  ]

  // --- Action Handlers ---

  function openAddDialog() {
    selectedWeight.value = null
    isEditing.value = false
    showDialog.value = true
  }

  function openEditDialog(item: WeightEntry) {
    selectedWeight.value = item
    isEditing.value = true
    showDialog.value = true
  }

  function openDeleteDialog(item: WeightEntry) {
    selectedWeight.value = item
    showConfirm.value = true
  }

  function closeConfirm() {
    showConfirm.value = false
    setTimeout(() => (selectedWeight.value = null), 200)
  }

  // --- Data Saving ---

  async function handleWeightSaved({
    processedValue,
    date,
  }: {
    processedValue: number
    date: string
  }) {
    const measuredAt = date
      ? new Date(
          date + 'T' + new Date().toTimeString().split(' ')[0]
        ).toISOString()
      : new Date().toISOString()

    try {
      if (isEditing.value && selectedWeight.value) {
        // Update existing
        await weightStore.updateWeight(
          selectedWeight.value.id,
          props.goatId,
          processedValue,
          measuredAt
        )
        showToastNotification(t('common.toasts.saved'), 'saved', 3000)
      } else {
        // Create new
        await weightStore.addWeight(props.goatId, processedValue, measuredAt)
        showToastNotification(t('views.session.valueSaved'), 'saved', 3000)
      }

      showDialog.value = false
      await props.refreshWeights()
    } catch (error) {
      console.error('Failed to save weight:', error)
      showToastNotification(t('views.session.savingError'), 'error', 5000)
    }
  }

  async function deleteConfirmed() {
    if (!selectedWeight.value) return
    try {
      await weightStore.deleteWeight(selectedWeight.value.id, props.goatId)
      await props.refreshWeights()
      closeConfirm()
      showToastNotification(t('common.toasts.saved'), 'info')
    } catch (e) {
      console.error('Failed to delete:', e)
      showToastNotification(t('views.session.savingError'), 'error')
    }
  }

  // --- Camera Logic ---

  function openCamera() {
    if (photoWeightRef.value) {
      // @ts-ignore
      photoWeightRef.value.open()
    }
  }

  async function handleWeightResult(result: any) {
    if (result && result.weight) {
      const measuredAt = new Date().toISOString()
      try {
        await weightStore.addWeight(props.goatId, result.weight, measuredAt)
        await props.refreshWeights()
        showToastNotification(t('views.session.valueSaved'), 'saved', 3000)
      } catch (error) {
        console.error('Failed to add weight from photo:', error)
        showToastNotification(t('views.session.savingError'), 'error', 5000)
      }
    }
  }

  function handlePhotoWeightError(error: any) {
    showToastNotification(error.message, error.type, 5000, error.subtitle)
  }

  function showToastNotification(
    message: string,
    type = 'saved',
    duration = 3000,
    subtitle = ''
  ) {
    showToast.value = false
    toastMessage.value = message
    toastType.value = type
    toastDuration.value = duration
    toastSubtitle.value = subtitle
    setTimeout(() => {
      showToast.value = true
    }, 10)
  }
</script>
